import { useMemo, useState } from "react";
import {
  Search,
  SlidersHorizontal,
  ChevronRight,
  Check,
  X,
} from "lucide-react";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../components/ui/command";
import { SessionStatusFilter } from "../../../../hooks/useControlsV2";
import { FilterOptions } from "../../../../models/audit/control";
import { cn } from "../../../../lib/utils";
import { TestPhase, TestPhases } from "../../../../models/audit/controlSession";
import { WorkStepFilterOptions } from "../../../../hooks/useWorkStepFilterOptions";

interface FilterCategory {
  id: string;
  label: string;
  /** Which entity this filter applies to. Unset means it applies to both. */
  group?: "Controls" | "Work Steps";
  options: Array<{ label: string; value: string }>;
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
}

export function FieldworkFilterBar({
  searchTerm,
  onSearchTermChange,
  testPhaseFilters,
  onTestPhaseFiltersChange,
  cycleFilters,
  onCycleFiltersChange,
  processFilters,
  onProcessFiltersChange,
  controlOwnerFilters,
  onControlOwnerFiltersChange,
  sessionStatusFilters,
  onSessionStatusFiltersChange,
  testCreatorFilters,
  onTestCreatorFiltersChange,
  fyCycleIdFilters,
  onFyCycleIdFiltersChange,
  filterOptions,
  workStepFilterOptions,
  sectionFilters,
  onSectionFiltersChange,
  subsectionFilters,
  onSubsectionFiltersChange,
  preparerFilters,
  onPreparerFiltersChange,
  opsAuditIdFilters,
  onOpsAuditIdFiltersChange,
  isFiltersOpen,
  onFiltersOpenChange,
  activeFilterCount,
  hasActiveFilters,
  onClearFilters,
  searchPlaceholder,
}: {
  searchTerm: string;
  onSearchTermChange: (v: string) => void;
  testPhaseFilters: TestPhase[];
  onTestPhaseFiltersChange: (v: TestPhase[]) => void;
  cycleFilters: string[];
  onCycleFiltersChange: (v: string[]) => void;
  processFilters: string[];
  onProcessFiltersChange: (v: string[]) => void;
  controlOwnerFilters: string[];
  onControlOwnerFiltersChange: (v: string[]) => void;
  sessionStatusFilters: SessionStatusFilter[];
  onSessionStatusFiltersChange: (v: SessionStatusFilter[]) => void;
  testCreatorFilters: string[];
  onTestCreatorFiltersChange: (v: string[]) => void;
  fyCycleIdFilters: string[];
  onFyCycleIdFiltersChange: (v: string[]) => void;
  filterOptions: FilterOptions | null;
  workStepFilterOptions?: WorkStepFilterOptions | null;
  sectionFilters?: string[];
  onSectionFiltersChange?: (v: string[]) => void;
  subsectionFilters?: string[];
  onSubsectionFiltersChange?: (v: string[]) => void;
  preparerFilters?: string[];
  onPreparerFiltersChange?: (v: string[]) => void;
  opsAuditIdFilters?: string[];
  onOpsAuditIdFiltersChange?: (v: string[]) => void;
  isFiltersOpen: boolean;
  onFiltersOpenChange: (open: boolean) => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  /** Defaults to the controls-only wording for the flag-off FieldworkPage;
      the unified page passes a both-entity phrasing. */
  searchPlaceholder?: string;
}) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categorySearch, setCategorySearch] = useState("");
  const [optionSearch, setOptionSearch] = useState("");

  const filterCategories: FilterCategory[] = useMemo(
    () => [
      {
        id: "session_status",
        label: "Session Status",
        options: [
          { label: "In Progress", value: "in_progress" },
          { label: "Pending Review", value: "in_review" },
          { label: "Complete", value: "complete" },
        ],
        selectedValues: sessionStatusFilters,
        onSelectionChange: (values) =>
          onSessionStatusFiltersChange(
            values.filter(
              (v): v is SessionStatusFilter =>
                v === "in_progress" || v === "in_review" || v === "complete",
            ),
          ),
      },
      {
        id: "test_phase",
        label: "Test Phase",
        group: "Controls",
        options: TestPhases.map((tp) => ({ label: tp.label, value: tp.value })),
        selectedValues: testPhaseFilters,
        onSelectionChange: (values) =>
          onTestPhaseFiltersChange(
            values.filter((v): v is TestPhase =>
              TestPhases.some((tp) => tp.value === v),
            ),
          ),
      },
      {
        id: "cycle",
        label: "Cycle",
        group: "Controls",
        options:
          filterOptions?.cycles.map((c) => ({ label: c, value: c })) ?? [],
        selectedValues: cycleFilters,
        onSelectionChange: onCycleFiltersChange,
      },
      {
        id: "process",
        label: "Process",
        group: "Controls",
        options:
          filterOptions?.processes.map((p) => ({ label: p, value: p })) ?? [],
        selectedValues: processFilters,
        onSelectionChange: onProcessFiltersChange,
      },
      {
        id: "owner",
        label: "Owner",
        group: "Controls",
        options:
          filterOptions?.controlOwners.map((o) => ({ label: o, value: o })) ??
          [],
        selectedValues: controlOwnerFilters,
        onSelectionChange: onControlOwnerFiltersChange,
      },
      {
        id: "test_creator",
        label: "Test Creator",
        options:
          filterOptions?.testCreators.map((c) => ({
            label: c,
            value: c,
          })) ?? [],
        selectedValues: testCreatorFilters,
        onSelectionChange: onTestCreatorFiltersChange,
      },
      {
        id: "fy_cycle",
        label: "FY",
        group: "Controls",
        options:
          filterOptions?.fyCycles?.map((c) => ({
            label: c.name,
            value: c.id,
          })) ?? [],
        selectedValues: fyCycleIdFilters,
        onSelectionChange: onFyCycleIdFiltersChange,
      },
      ...(workStepFilterOptions
        ? [
            {
              id: "section",
              label: "Section",
              group: "Work Steps" as const,
              options: workStepFilterOptions.sections.map((s) => ({
                label: s,
                value: s,
              })),
              selectedValues: sectionFilters ?? [],
              onSelectionChange: onSectionFiltersChange ?? (() => {}),
            },
            {
              id: "subsection",
              label: "Subsection",
              group: "Work Steps" as const,
              options: workStepFilterOptions.subsections.map((s) => ({
                label: s,
                value: s,
              })),
              selectedValues: subsectionFilters ?? [],
              onSelectionChange: onSubsectionFiltersChange ?? (() => {}),
            },
            {
              id: "preparer",
              label: "Preparer",
              group: "Work Steps" as const,
              options: workStepFilterOptions.preparers.map((p) => ({
                label: p,
                value: p,
              })),
              selectedValues: preparerFilters ?? [],
              onSelectionChange: onPreparerFiltersChange ?? (() => {}),
            },
            {
              id: "ops_audit",
              label: "Audits",
              group: "Work Steps" as const,
              options: workStepFilterOptions.opsAudits.map((a) => ({
                label: a.name,
                value: a.id,
              })),
              selectedValues: opsAuditIdFilters ?? [],
              onSelectionChange: onOpsAuditIdFiltersChange ?? (() => {}),
            },
          ]
        : []),
    ],
    [
      filterOptions,
      testPhaseFilters,
      onTestPhaseFiltersChange,
      sessionStatusFilters,
      onSessionStatusFiltersChange,
      cycleFilters,
      onCycleFiltersChange,
      processFilters,
      onProcessFiltersChange,
      controlOwnerFilters,
      onControlOwnerFiltersChange,
      testCreatorFilters,
      onTestCreatorFiltersChange,
      fyCycleIdFilters,
      onFyCycleIdFiltersChange,
      workStepFilterOptions,
      sectionFilters,
      onSectionFiltersChange,
      subsectionFilters,
      onSubsectionFiltersChange,
      preparerFilters,
      onPreparerFiltersChange,
      opsAuditIdFilters,
      onOpsAuditIdFiltersChange,
    ],
  );

  const currentCategory = filterCategories.find(
    (c) => c.id === selectedCategory,
  );

  // Group categories ungrouped-first, then Controls, then Work Steps, so a
  // heading can introduce each entity-specific run. Only repartition when a
  // Work Steps category actually exists: session_status and test_creator are
  // intentionally left ungrouped (Step 2) because they apply to both
  // entities, but they sit *between* Controls-tagged categories in the array
  // above. Partitioning unconditionally would pull them to the front and
  // reorder the frozen path (no workStepFilterOptions ever reaches this
  // component) even though no heading would show — so instead, with no work
  // steps, render filterCategories exactly as-is: one ungrouped run in its
  // original order, identical to before this task.
  const { groupedCategories, showHeadings } = useMemo(() => {
    const workSteps = filterCategories.filter((c) => c.group === "Work Steps");
    if (workSteps.length === 0) {
      return {
        groupedCategories: [{ heading: null, categories: filterCategories }],
        showHeadings: false,
      };
    }
    const ungrouped = filterCategories.filter((c) => !c.group);
    const controls = filterCategories.filter((c) => c.group === "Controls");
    // Headings exist to disambiguate *entities*, and there is only one entity
    // to name until work steps are present (handled by the branch above) —
    // not `groups.length > 1`, since ungrouped + Controls alone is already
    // two groups.
    const groups = [
      { heading: null, categories: ungrouped },
      { heading: "Controls", categories: controls },
      { heading: "Work Steps", categories: workSteps },
    ].filter((g) => g.categories.length > 0);
    return { groupedCategories: groups, showHeadings: true };
  }, [filterCategories]);

  const filteredOptions = currentCategory?.options.filter((o) =>
    o.label.toLowerCase().includes(optionSearch.toLowerCase()),
  );

  const handleOptionToggle = (value: string) => {
    if (!currentCategory) return;
    const isSelected = currentCategory.selectedValues.includes(value);
    currentCategory.onSelectionChange(
      isSelected
        ? currentCategory.selectedValues.filter((v) => v !== value)
        : [...currentCategory.selectedValues, value],
    );
  };

  const handleOpenChange = (open: boolean) => {
    onFiltersOpenChange(open);
    if (!open) {
      setSelectedCategory(null);
      setCategorySearch("");
      setOptionSearch("");
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Popover open={isFiltersOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            data-testid="fieldwork-filters-button"
            variant="outline"
            size="icon"
            className={cn(
              "shrink-0",
              activeFilterCount > 0 && "border-stone-900",
            )}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-0" align="start" sideOffset={8}>
          <div className="flex h-[350px]">
            <Command className="w-1/2 border-r">
              <CommandInput
                placeholder="Filter..."
                value={categorySearch}
                onValueChange={setCategorySearch}
              />
              <CommandList>
                <CommandEmpty>No categories found</CommandEmpty>
                {groupedCategories.map((group) => {
                  const categoriesInGroup = group.categories.filter((c) =>
                    c.label
                      .toLowerCase()
                      .includes(categorySearch.toLowerCase()),
                  );
                  if (categoriesInGroup.length === 0) return null;
                  return (
                    <CommandGroup key={group.heading ?? "ungrouped"}>
                      {showHeadings && group.heading && (
                        <div className="px-2 py-1.5 text-xs font-medium text-stone-500">
                          {group.heading}
                        </div>
                      )}
                      {categoriesInGroup.map((category) => (
                        <CommandItem
                          key={category.id}
                          value={category.label}
                          onMouseEnter={() => {
                            setSelectedCategory(category.id);
                            setOptionSearch("");
                          }}
                          onSelect={() => {
                            setSelectedCategory(category.id);
                            setOptionSearch("");
                          }}
                          className={cn(
                            "flex items-center justify-between cursor-pointer",
                            selectedCategory === category.id && "bg-stone-100",
                          )}
                        >
                          <span
                            className={cn(
                              category.selectedValues.length > 0 &&
                                "font-medium",
                            )}
                          >
                            {category.label}
                            {category.selectedValues.length > 0 && (
                              <span className="ml-1.5 text-xs text-stone-500">
                                ({category.selectedValues.length})
                              </span>
                            )}
                          </span>
                          <ChevronRight className="h-4 w-4 text-stone-400" />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>

            <Command className="w-1/2">
              <CommandInput
                placeholder="Filter..."
                value={optionSearch}
                onValueChange={setOptionSearch}
              />
              <CommandList>
                {!currentCategory ? (
                  <div className="py-6 text-center text-sm text-stone-500">
                    Select a category
                  </div>
                ) : filteredOptions && filteredOptions.length > 0 ? (
                  <CommandGroup>
                    {filteredOptions.map((option) => {
                      const isSelected =
                        currentCategory.selectedValues.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.label}
                          onSelect={() => handleOptionToggle(option.value)}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <div
                            className={cn(
                              "flex h-4 w-4 items-center justify-center rounded-sm border border-stone-300",
                              isSelected && "bg-stone-900 border-stone-900",
                            )}
                          >
                            {isSelected && (
                              <Check className="h-3 w-3 text-white" />
                            )}
                          </div>
                          <span>{option.label}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : (
                  <CommandEmpty>No options found</CommandEmpty>
                )}
              </CommandList>
            </Command>
          </div>
        </PopoverContent>
      </Popover>

      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 pointer-events-none" />
        <Input
          placeholder={
            searchPlaceholder ?? "Search tests by control, name, or system..."
          }
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          className="pl-10"
        />
      </div>

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearFilters}
          className="shrink-0 gap-1 text-stone-500 hover:text-stone-900"
        >
          <X className="h-4 w-4" />
          Clear
        </Button>
      )}
    </div>
  );
}
